// src/components/Profile.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const Profile = () => {
  const { currentUser } = useAuth();
  const [profileData, setProfileData] = useState({
    bio: currentUser?.bio || '',
    favorite_game: currentUser?.favorite_game || '',
    twitter_handle: currentUser?.twitter_handle || '',
    twitch_handle: currentUser?.twitch_handle || '',
    discord_handle: currentUser?.discord_handle || '',
    reddit_handle: currentUser?.reddit_handle || ''
  });
  
  // Track which field is currently being edited
  const [editField, setEditField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Format the date properly, providing a fallback for new users
  const formatDate = (dateString) => {
    if (!dateString) {
      // If no date is provided, use current time and mark as "Just joined"
      if (!currentUser.created_at) {
        // Update user object with current date if it's missing
        const now = new Date();
        if (currentUser && !currentUser.hasOwnProperty('created_at')) {
          currentUser.created_at = now.toISOString();
        }
      }
      return 'Just joined';
    }
    
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      console.error("Error formatting date:", e);
      return 'Just joined';
    }
  };
  
  useEffect(() => {
    // Update local state when currentUser changes
    if (currentUser) {
      setProfileData({
        bio: currentUser.bio || '',
        favorite_game: currentUser.favorite_game || '',
        twitter_handle: currentUser.twitter_handle || '',
        twitch_handle: currentUser.twitch_handle || '',
        discord_handle: currentUser.discord_handle || '',
        reddit_handle: currentUser.reddit_handle || ''
      });
    }
  }, [currentUser]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (field = null) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const response = await fetch('/api/user/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update profile');
      }
      
      setSuccess(true);
      setEditField(null); // Exit edit mode
      
      // Wait for success message to be displayed before clearing it
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to render editable fields
  const renderEditableField = (fieldName, label, type = 'text', placeholder = '') => {
    const isEditing = editField === fieldName;
    
    // For empty bio field
    if (fieldName === 'bio' && !profileData.bio && !isEditing) {
      return (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{label}</h2>
          <button
            onClick={() => setEditField(fieldName)}
            className="text-gray-400 hover:text-blue-400 text-sm border border-dashed border-gray-600 py-4 px-3 rounded-md w-full text-left"
          >
            Add a bio to your profile...
          </button>
        </div>
      );
    }
    
    // For empty favorite game field
    if (fieldName === 'favorite_game' && !profileData.favorite_game && !isEditing) {
      return (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">{label}</h2>
          <button
            onClick={() => setEditField(fieldName)}
            className="text-gray-400 hover:text-blue-400 text-sm border border-dashed border-gray-600 py-2 px-3 rounded-md"
          >
            Add your favorite game
          </button>
        </div>
      );
    }
    
    // For regular fields
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          {label}
          {!isEditing && (
            <button
              onClick={() => setEditField(fieldName)}
              className="ml-2 text-sm text-gray-400 hover:text-blue-400"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </h2>
        
        {isEditing ? (
          <div>
            {type === 'textarea' ? (
              <textarea
                name={fieldName}
                value={profileData[fieldName]}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={placeholder}
              ></textarea>
            ) : (
              <input
                type={type}
                name={fieldName}
                value={profileData[fieldName]}
                onChange={handleInputChange}
                className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={placeholder}
              />
            )}
            
            <div className="flex space-x-2 mt-2">
              <button
                type="button"
                onClick={() => handleSubmit(fieldName)}
                disabled={loading}
                className={`px-3 py-1 rounded-md text-sm text-white ${
                  loading
                    ? 'bg-blue-700 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditField(null)}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded-md text-sm hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-gray-300 whitespace-pre-line">{profileData[fieldName]}</p>
        )}
      </div>
    );
  };
  
  // Handle social media fields separately
  const renderSocialSection = () => {
    const isEditing = editField === 'social';
    const hasSocialHandles = profileData.twitter_handle || profileData.twitch_handle || 
                            profileData.discord_handle || profileData.reddit_handle;
    
    // If no social handles and not editing, show add button
    if (!hasSocialHandles && !isEditing) {
      return (
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Social Handles</h2>
          <button
            onClick={() => setEditField('social')}
            className="text-gray-400 hover:text-blue-400 text-sm border border-dashed border-gray-600 py-2 px-3 rounded-md"
          >
            Add your social media handles
          </button>
        </div>
      );
    }
    
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-2">
          Social Handles
          {!isEditing && (
            <button
              onClick={() => setEditField('social')}
              className="ml-2 text-sm text-gray-400 hover:text-blue-400"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </h2>
        
        {isEditing ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Twitter/X
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-600 bg-gray-600 text-gray-300">
                    @
                  </span>
                  <input
                    type="text"
                    name="twitter_handle"
                    value={profileData.twitter_handle}
                    onChange={handleInputChange}
                    className="flex-1 p-2 rounded-r-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="username"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Twitch
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-600 bg-gray-600 text-gray-300">
                    twitch.tv/
                  </span>
                  <input
                    type="text"
                    name="twitch_handle"
                    value={profileData.twitch_handle}
                    onChange={handleInputChange}
                    className="flex-1 p-2 rounded-r-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="username"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Discord
                </label>
                <input
                  type="text"
                  name="discord_handle"
                  value={profileData.discord_handle}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="username#0000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reddit
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-600 bg-gray-600 text-gray-300">
                    u/
                  </span>
                  <input
                    type="text"
                    name="reddit_handle"
                    value={profileData.reddit_handle}
                    onChange={handleInputChange}
                    className="flex-1 p-2 rounded-r-md border border-gray-700 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="username"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex space-x-2 mt-2">
              <button
                type="button"
                onClick={() => handleSubmit('social')}
                disabled={loading}
                className={`px-3 py-1 rounded-md text-sm text-white ${
                  loading
                    ? 'bg-blue-700 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditField(null)}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded-md text-sm hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profileData.twitter_handle && (
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.01l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                </svg>
                <span className="text-gray-300">{profileData.twitter_handle}</span>
              </div>
            )}
            
            {profileData.twitch_handle && (
              <div className="flex items-center">
                <svg className="w-5 h-5 text-purple-400 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11.64 5.93h1.43v4.28h-1.43m3.93-4.28H17v4.28h-1.43M7 2L3.43 5.57v12.86h4.28V22l3.58-3.57h2.85L20.57 12V2m-1.43 9.29l-2.85 2.85h-2.86l-2.5 2.5v-2.5H7.71V3.43h11.43z"></path>
                </svg>
                <span className="text-gray-300">{profileData.twitch_handle}</span>
              </div>
            )}
            
            {profileData.discord_handle && (
              <div className="flex items-center">
                <svg className="w-5 h-5 text-indigo-400 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.39-.444.885-.608 1.283a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.283.077.077 0 0 0-.079-.036c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 13.83 13.83 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.201 13.201 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.183 0-2.157-1.068-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.068-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z"></path>
                </svg>
                <span className="text-gray-300">{profileData.discord_handle}</span>
              </div>
            )}
            
            {profileData.reddit_handle && (
              <div className="flex items-center">
                <svg className="w-5 h-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-18c4.411 0 8 3.589 8 8s-3.589 8-8 8-8-3.589-8-8 3.589-8 8-8zm6.744 9.333a1.803 1.803 0 0 0-1.802-1.802c-.492 0-.926.199-1.245.506-1.144-.818-2.675-1.349-4.372-1.418l.891-2.846 2.417.556a1.347 1.347 0 0 0 2.658-.291c0-.738-.598-1.336-1.336-1.336-.564 0-1.045.352-1.243.844l-2.55-.586a.375.375 0 0 0-.446.233l-1.17 3.733c-1.7.078-3.228.601-4.371 1.418a1.78 1.78 0 0 0-1.245-.506 1.803 1.803 0 0 0-1.802 1.802c0 .718.424 1.333 1.033 1.616a3.43 3.43 0 0 0-.06.618c0 2.342 2.85 4.24 6.365 4.24 3.514 0 6.364-1.898 6.364-4.24 0-.209-.022-.413-.06-.618a1.8 1.8 0 0 0 1.033-1.616zm-9.155.764c0-.738.598-1.336 1.336-1.336.739 0 1.336.598 1.336 1.336 0 .738-.598 1.336-1.336 1.336-.738 0-1.336-.598-1.336-1.336zm6.155 3.811c-.587.587-1.688.913-3.142.913-1.455 0-2.556-.326-3.142-.913a.375.375 0 0 1 .53-.53c.392.392 1.287.666 2.612.666 1.324 0 2.22-.273 2.612-.666a.375.375 0 0 1 .53.53zm-.155-2.475c-.738 0-1.336-.598-1.336-1.336 0-.738.598-1.336 1.336-1.336.739 0 1.336.598 1.336 1.336 0 .738-.598 1.336-1.336 1.336z"></path>
                </svg>
                <span className="text-gray-300">{profileData.reddit_handle}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="bg-gray-900 py-8 text-gray-100">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-blue-400 mb-6">Your Profile</h1>
        
        {error && (
          <div className="mb-6 bg-red-900/30 border border-red-700 p-3 rounded-md text-red-200">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-900/30 border border-green-700 p-3 rounded-md text-green-200">
            Profile updated successfully!
          </div>
        )}
        
        <div className="bg-gray-800 rounded-lg shadow border border-gray-700 p-6">
          {/* Account information - not editable */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">Account Information</h2>
            <p className="text-gray-300"><span className="font-medium text-gray-200">Username:</span> {currentUser.username}</p>
            <p className="text-gray-300"><span className="font-medium text-gray-200">Email:</span> {currentUser.email}</p>
            <p className="text-gray-300">
              <span className="font-medium text-gray-200">Joined:</span> {formatDate(currentUser.created_at)}
            </p>
          </div>
          
          {/* Editable fields */}
          {renderEditableField('bio', 'Bio', 'textarea', 'Tell us about yourself...')}
          {renderEditableField('favorite_game', 'Favorite Game', 'text', 'What\'s your favorite board game?')}
          {renderSocialSection()}
        </div>
      </div>
    </div>
  );
};

export default Profile;